import { useState, useRef, useEffect } from 'react';
import { Card, CardHeader, CardContent, CardFooter } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Sparkles, Send, User, Zap } from 'lucide-react';
import { Input } from '../../../components/Input';
import { Avatar } from '../../../components/Avatar';
import apiClient from '../../../lib/api';

interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
}

export function AiWorkAssistant() {
    const [messages, setMessages] = useState<Message[]>([
        {
            id: '1',
            role: 'assistant',
            content: 'Hello! I am your AI Work Assistant. I can help you draft specs, summarize incidents, manage projects, and find documents. What are we working on today?',
            timestamp: new Date(),
        }
    ]);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const endOfMessagesRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        endOfMessagesRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isTyping]);

    const handleSend = async () => {
        if (!input.trim() || isTyping) return;

        const question = input.trim();
        const userMsg: Message = {
            id: Date.now().toString(),
            role: 'user',
            content: question,
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setIsTyping(true);

        try {
            const res = await apiClient.post('/work/ai/chat', { message: question });
            const reply = res.data?.reply || 'No response.';
            setMessages(prev => [...prev, {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: reply,
                timestamp: new Date(),
            }]);
        } catch (err: any) {
            setMessages(prev => [...prev, {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: err?.response?.data?.message || 'Sorry — I could not reach the assistant. Please try again.',
                timestamp: new Date(),
            }]);
        } finally {
            setIsTyping(false);
        }
    };

    return (
        <div className="space-y-6 h-[calc(100vh-8rem)] flex flex-col">
            <div className="flex justify-between items-center shrink-0">
                <div>
                    <h2 className="tracking-tight text-foreground flex items-center gap-2 font-display text-section-title">
                        <Sparkles className="w-6 h-6 text-primary" />
                        AI Work Assistant
                    </h2>
                    <p className="text-muted-foreground">Your agentic copilot for the entire work lifecycle.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 flex-1 min-h-0">
                {/* Sidebar with suggested prompts */}
                <Card className="md:col-span-1 flex flex-col hidden md:flex">
                    <CardHeader>
                        <h3 className="text-foreground flex items-center gap-2 font-display text-card-title">
                            <Zap className="w-4 h-4 text-warning" />
                            Suggested Actions
                        </h3>
                    </CardHeader>
                    <CardContent className="space-y-2 flex-1 overflow-y-auto">
                        <Button variant="outline" className="w-full justify-start text-left whitespace-normal h-auto py-2 text-xs" onClick={() => setInput("Summarize the latest active incident.")}>
                            Summarize the latest active incident
                        </Button>
                        <Button variant="outline" className="w-full justify-start text-left whitespace-normal h-auto py-2 text-xs" onClick={() => setInput("Draft a project spec for 'Dark Mode'.")}>
                            Draft a project spec for 'Dark Mode'
                        </Button>
                        <Button variant="outline" className="w-full justify-start text-left whitespace-normal h-auto py-2 text-xs" onClick={() => setInput("What are my pending approvals?")}>
                            What are my pending approvals?
                        </Button>
                        <Button variant="outline" className="w-full justify-start text-left whitespace-normal h-auto py-2 text-xs" onClick={() => setInput("Find notes from the 'Q4 Planning' meeting.")}>
                            Find notes from 'Q4 Planning'
                        </Button>
                    </CardContent>
                </Card>

                {/* Chat Area */}
                <Card className="md:col-span-3 flex flex-col bg-card">
                    <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                        {messages.map((msg) => (
                            <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                {msg.role === 'assistant' && (
                                    <Avatar size="sm" className="bg-primary/10 text-primary shrink-0">
                                        <Sparkles className="w-4 h-4" />
                                    </Avatar>
                                )}
                                <div className={`max-w-[80%] rounded-lg p-3 ${
                                    msg.role === 'user' 
                                        ? 'bg-primary text-primary-foreground rounded-tr-sm' 
                                        : 'bg-secondary text-foreground rounded-tl-sm'
                                }`}>
                                    <p className="text-sm">{msg.content}</p>
                                    <p className={`text-xs mt-1 ${msg.role === 'user' ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                                {msg.role === 'user' && (
                                    <Avatar size="sm" className="bg-secondary text-foreground shrink-0">
                                        <User className="w-4 h-4" />
                                    </Avatar>
                                )}
                            </div>
                        ))}
                        {isTyping && (
                            <div className="flex gap-3 justify-start">
                                <Avatar size="sm" className="bg-primary/10 text-primary shrink-0">
                                    <Sparkles className="w-4 h-4" />
                                </Avatar>
                                <div className="bg-secondary rounded-lg rounded-tl-sm p-4 flex gap-1 items-center">
                                    <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                    <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                    <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce"></div>
                                </div>
                            </div>
                        )}
                        <div ref={endOfMessagesRef} />
                    </CardContent>
                    <CardFooter className="p-4 border-t border-border bg-card">
                        <form 
                            onSubmit={(e) => { e.preventDefault(); handleSend(); }} 
                            className="flex w-full gap-2 items-center"
                        >
                            <Input 
                                placeholder="Ask Work Assistant..." 
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                className="flex-1"
                            />
                            <Button type="submit" variant="primary" size="icon" disabled={!input.trim() || isTyping}>
                                <Send className="w-4 h-4" />
                            </Button>
                        </form>
                    </CardFooter>
                </Card>
            </div>
        </div>
    );
}
